import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SidebarTabs from 'components/SidebarTabs/SidebarTabs';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import { dropActiveApplicationDetails } from 'features/applications/applicationsSlice';

import { useGetActiveSidebarItemId } from 'hooks/useGetActiveSidebarItemId';
import { useAppDispatch } from 'hooks/useTypedRedux';

import { SidebarTabsItem } from 'types/sidebar';

import ApplicationResumeView from '../ApplicationResumeView/ApplicationResumeView';
import ApplicationView from '../ApplicationView/ApplicationView';
import InterviewView from '../InterviewView/InterviewView';
import OfferView from '../OfferView/OfferView';
import useGetApplication from '../hooks/useGetApplication';

export const APPLICATION_SIDEBAR_ITEMS_IDS = {
  APPLICATION: 1,
  OFFER: 2,
  INTERVIEW: 3,
  RESUME: 4
};

interface ApplicationSidebarProps {
  openItemId?: number;
}

const ApplicationSidebar: FC<ApplicationSidebarProps> = ({
  openItemId
}: ApplicationSidebarProps) => {
  const { isLoading, fetchApplicationData } = useGetApplication();
  const activeApplicationId = useSelector(selectActiveUserId);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const allowedTabs = new Set<number>(
    Object.values(APPLICATION_SIDEBAR_ITEMS_IDS)
  );
  const activeItemId = useGetActiveSidebarItemId(allowedTabs, openItemId);

  const items: SidebarTabsItem[] = useMemo(
    () => [
      {
        id: APPLICATION_SIDEBAR_ITEMS_IDS.APPLICATION,
        title: t('sidebar.application.tab.main'),
        component: <ApplicationView isLoading={isLoading} />,
        icon: null
      },
      {
        id: APPLICATION_SIDEBAR_ITEMS_IDS.INTERVIEW,
        title: t('sidebar.application.tab.interview'),
        component: (
          <InterviewView
            applicationId={activeApplicationId ?? null}
            fetchApplicationData={fetchApplicationData}
            isSidebarMode
          />
        ),
        icon: null
      },
      {
        id: APPLICATION_SIDEBAR_ITEMS_IDS.OFFER,
        title: t('sidebar.application.tab.offer'),
        component: (
          <OfferView
            isLoading={isLoading}
            fetchApplicationData={fetchApplicationData}
          />
        ),
        icon: null
      },
      {
        id: APPLICATION_SIDEBAR_ITEMS_IDS.RESUME,
        title: t('sidebar.application.tab.resume'),
        component: (
          <ApplicationResumeView
            isLoading={isLoading}
            fetchApplicationData={fetchApplicationData}
          />
        ),
        icon: null
      }
    ],
    [activeApplicationId, fetchApplicationData, isLoading, t]
  );

  useEffect(() => {
    return () => {
      dispatch(dropActiveApplicationDetails());
    };
  }, []);

  return (
    <div>
      <SidebarTabs
        items={items}
        isChangeCurrentTabOnNewItems={false}
        openItemId={activeItemId}
      />
    </div>
  );
};

export default ApplicationSidebar;
